import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import {
    AuthenticationService, CommonUtil,
    CookieService, CookieUtil,
    Platform,
    CommonPlatformService,
    SelectUserDTO, PlatformValidityEnum
} from 'psd-common';
import * as HttpStatus from 'http-status-codes';
import {FORBIDDEN} from "http-status-codes";

@Component({
    selector: 'central-login-login',
    templateUrl: 'central-login-login.component.html'
})
export class CentralLoginLoginComponent implements OnInit {
    public loginFormFirstStep: FormGroup;
    public loginFormSecondStep: FormGroup;
    public loginFormMFAStep: FormGroup;
    public firstFormSubmitted: boolean = false;
    public mfaFormSubmitted: boolean = false;
    public submitted: boolean = false;
    public platformPrefix: string;
    public platformDomainExtension: string;
    public currentPlatform: Platform;
    public loginFailure: boolean = false;
    public submitting: boolean = false;
    public emailGiven: boolean = false;
    public isMFA: boolean = false;
    public submittingMFA: boolean = false;
    public emailErrorNotExisting: boolean = false;
    public emailErrorNotVerified: boolean = false;
    public emailErrorNotActive: boolean = false;
    public emailError: boolean = false;
    public emailErrorMessage: string;
    public mfaIp: string;
    public mfaClientData: string;
    public mfaChallenge: string;
    public mfaSessionId: string;
    public mfaOrigin: string;
    public email: string;
    public password: string;
    public centralAccessToken: string;
    public centralRefreshToken: string;
    public renderLoginForm: boolean = false;
    public scope: string = '';
    public redirectUrl: string = '';
    public invalidPlatform:boolean = false;
    public globalAdmin:boolean = false;

    constructor(@Inject(DOCUMENT) private document: Document,
                private renderer: Renderer2,
                private cookieService: CookieService,
                private router: Router,
                private formBuilder: FormBuilder,
                private authenticationService: AuthenticationService,
                private route: ActivatedRoute,
                private platformService: CommonPlatformService,
                private httpClient: HttpClient,
                private translateService: TranslateService) {}

    ngOnInit() {
        this.renderer.addClass(this.document.body, 'login');

        this.route.queryParams.pipe(mergeMap(_params => {
            this.platformPrefix = _params.platform;
            this.email = _params.email;
            this.scope = _params.scope;
            this.redirectUrl = _params.redirectUrl;

            if(this.platformPrefix) {
                return this.platformService.getExternalPlatformDetails(this.platformPrefix);
            } else {
                return of(null);
            }
        })).pipe(mergeMap(_platform => {
            if (_platform) {
                this.currentPlatform = _platform;
                return this.platformService.executeGetPlatformValidity({platformId: this.currentPlatform.id, regionId: this.currentPlatform.regionId});
            } else {
                this.currentPlatform = null;
                return of(null);
            }

        })).pipe(mergeMap((validity: PlatformValidityEnum) => {
            if(validity == PlatformValidityEnum.PLATFORM_INVALID){
                this.invalidPlatform = true;
                return of(null);

            } else {
                return this.authenticationService.isLoggedIn(environment.production);
            }
        })).subscribe(_loggedIn => {
            if (!this.invalidPlatform) {
                if(_loggedIn) {
                    this.loadUsersAndProcessLoginRedirect();
                } else {
                    // build the login form
                    this.loginFormFirstStep = this.formBuilder.group({
                        email: ['', [Validators.required, Validators.email]]
                    }, {
                    });

                    this.loginFormSecondStep = this.formBuilder.group({
                        email: ['', [Validators.required, Validators.email]],
                        password: ['', [Validators.required]]
                    }, {
                    });

                    this.loginFormMFAStep = this.formBuilder.group({
                        code: ['', [Validators.required]]
                    }, {
                    });

                    if(this.email) {
                        this.loginFormFirstStep.setValue({'email' : this.email});
                    }

                    this.renderLoginForm = true;

                    setTimeout(function() {
                        document.getElementById('email').focus();
                    }, 100);
                }
            }
        });
    }

    loadUsersAndProcessLoginRedirect() {
        this.centralAccessToken = this.cookieService.getCentralCookie('central_access_token', environment.production);
        this.authenticationService.loadCurrentPossibleUsers(this.centralAccessToken, this.currentPlatform?.name).subscribe(_users => {
            if (_users && _users.length > 0 && this.currentPlatform) {
                // if platform prefix available and only one user
                if (_users.length == 1) {
                    let selectUserDTO = new SelectUserDTO();
                    selectUserDTO.userId = _users[0].platformUserId;
                    selectUserDTO.platformId = _users[0].platformId;
                    this.authenticationService.selectUser(selectUserDTO, this.centralAccessToken, _users[0].apiURL).subscribe(_selectUserResult => {
                        // @ts-ignore
                        let platformForm: HTMLFormElement = document.getElementById('goToPlatformForm');
                        platformForm.action = _users[0].platformURL + '/index.php';
                        platformForm['central_access_token'].value = this.centralAccessToken;
                        platformForm['central_refresh_token'].value = this.centralRefreshToken;
                        platformForm['access_token'].value = _selectUserResult.access_token;
                        platformForm['refresh_token'].value = _selectUserResult.refresh_token;
                        platformForm['expires_in'].value = _selectUserResult.expires_in;
                        platformForm['force_token_refresh'].value = 'true';
                        if (this.scope && this.scope === 'sportspay' && this.redirectUrl) {
                            platformForm['q'].value = this.redirectUrl;
                        }
                        platformForm.submit();
                    });
                } else {
                    // multiple users for this account, platform available => go to select-account page
                    this.router.navigate(['/central-login/select-account'], { queryParams: { platform: this.platformPrefix }});
                }
            } else if (!this.currentPlatform) {
                this.router.navigate(['/central-login/dashboard']);
            } else if (_users && _users.length == 0 && this.currentPlatform) {
                this.router.navigate(['/central-login/select-account'], { queryParams: { platform: this.platformPrefix, error: 'no-user' }});
            }
        }, _error => {
            if (this.currentPlatform && _error.status == HttpStatus.FORBIDDEN && _error.message === 'error_global_admin') {
                this.adminPlatformLogin('3600');
            } else {
                this.router.navigate(['/central-login/select-account'], { queryParams: { platform: this.platformPrefix, error: 'no-user' }});
            }
        });
    }

    get formFirstStep() { return this.loginFormFirstStep.controls; }

    get formSecondStep() { return this.loginFormSecondStep.controls; }

    get formMFAStep() { return this.loginFormMFAStep.controls; }

    continue() {
        this.firstFormSubmitted = true;

        // stop here if form is invalid
        if (this.loginFormFirstStep.invalid) {
            return;
        }

        let json = JSON.parse(JSON.stringify(this.loginFormFirstStep.value, null, 4));
        this.email = json.email;

        // do email check
        this.authenticationService.checkEmail(this.email, this.platformPrefix).subscribe(
          _result => {
            switch (_result.emailStatus) {
                case 'ACTIVE':
                    this.loginFormSecondStep.setValue({
                        'email': this.email,
                        'password': null
                    });
                    this.emailGiven = true;
                    this.globalAdmin = _result.globalAdmin;
                    setTimeout(function() {
                        document.getElementById('password').focus();
                    }, 5);

                    break;
                case 'NOT_EXISTING':
                    this.emailErrorNotExisting = true;
                    this.emailErrorMessage = _result.message;
                    break;
                case 'NOT_VERIFIED':
                    this.emailErrorNotVerified = true;
                    this.emailErrorMessage = _result.message;
                    break;
                case 'NOT_ACTIVE':
                    this.emailErrorNotActive = true;
                    this.emailErrorMessage = _result.message;

                    break;
                case 'SSO':
                    window.location.href = _result.redirectUrl;
                    break;
            }
        },
          _error => {
              this.emailError = true;
              this.emailErrorMessage = _error.message;
          });
    }

    togglePassword(event: any) {
        const password = document.querySelector('#password');
        const type = password.getAttribute('type') === 'password' ? 'text' : 'password';
        password.setAttribute('type', type);
        event.target.classList.toggle('fa-eye-slash');

    }

    login() {
        this.submitted = true;
        this.submitting = true;
        this.loginFailure = false;

        // stop here if form is invalid
        if (this.loginFormSecondStep.invalid) {
            this.submitting = false;
            return;
        }

        let json = JSON.parse(JSON.stringify(this.loginFormSecondStep.value, null, 4));
        this.password = json.password;

        //  *** login flow ***
        //  1.  login (with or without prefix param)
        //  2.  execute loadCurrentPossibleUsers (execute with access_token as header) to get all possible users for the central logged in user
        //  3.  if one result => execute selectUser with SelectUserDTO and access_token in header
        //          => if platform prefix available => forward/redirect to platform URL with the access_token  (returned from selectUser) as a http header
    //              => if no platform prefix available => forward to central login dashboard
        //  4.  if multiple results => redirect to select account page => choose user in UI
        //      => execute selectUser => forward/redirect to platform URL with the access_token  (returned from selectUser) as a http header

        if (this.globalAdmin) {
            this.httpClient.get('https://api.ipify.org/?format=json').subscribe(_ipResult => {
                // @ts-ignore
                this.mfaIp = _ipResult.ip;
                this.performAuthentication(json);
            }, error => {
                //perform authentication anyway with a default IP
                // the ip is only used for authentication of MFA (@psd.com)
                this.mfaIp = '127.0.0.1';
                this.performAuthentication(json);
            });
        } else {
            //perform authentication anyway with a default IP
            // the ip is only used for authentication of MFA (@psd.com)
            this.performAuthentication(json);
        }
    }

    performAuthentication(json: any) {
        // @ts-ignore
        this.mfaClientData = AmazonCognitoAdvancedSecurityData.getData(json.email, 'eu-west-1_WdiJszk0H', '');
        this.authenticationService.login('login', this.email, this.password, (this.platformPrefix != null ? this.platformPrefix : null), this.mfaClientData, this.mfaIp).subscribe(_result => {
            if(_result.status != 'failed') {
                this.centralAccessToken = _result.access_token;
                this.centralRefreshToken = _result.refresh_token;

                CookieUtil.setCentralCookies(this.centralAccessToken, this.centralRefreshToken, environment.production, _result.expires_in);

                localStorage.setItem('userEmail', json.email);
                this.loadUsersAndProcessLoginRedirect();
            } else {
                this.submitting = false;
                this.loginFailure = true;
            }
        }, _error => {
            this.submitting = false;
            // todo: do we display a common login error message or more specific?
            try {
                let message = JSON.parse(_error.message);
                // to use MFA you need to use a prefix to go to a specific platform!!
                this.mfaChallenge = message?.challenge;
                this.mfaSessionId = message?.sessionId;
                this.mfaOrigin = message?.origin;
                if (message && (message.challenge == 'SOFTWARE_TOKEN_MFA' || message.challenge == 'SMS_MFA') && this.platformPrefix) {
                    this.isMFA = true;
                } else {
                    this.loginFailure = true;
                }
            } catch (error) {
                this.loginFailure = true;
                this.submitting = false;
            }
        });
    }

    submitMFA() {
        this.submittingMFA = true;
        this.mfaFormSubmitted = true;

        if (this.loginFormMFAStep.invalid) {
            this.submittingMFA = false;
            return;
        }

        let json = JSON.parse(JSON.stringify(this.loginFormMFAStep.value, null, 4));

        this.authenticationService.login('mfa', this.email, this.password, this.platformPrefix, this.mfaClientData, this.mfaIp, this.mfaOrigin, json.code, this.mfaChallenge, this.mfaSessionId).subscribe(_mfaResult => {
            this.centralAccessToken = _mfaResult.access_token;
            this.centralRefreshToken = _mfaResult.refresh_token;

            CookieUtil.setCentralCookies(this.centralAccessToken, this.centralRefreshToken, environment.production, _mfaResult.expires_in);
            localStorage.setItem('userEmail', this.email);
            this.adminPlatformLogin(_mfaResult.expires_in);
        });
    }

    private adminPlatformLogin(expiresIn: string) {
        let baseURL = CommonUtil.getBaseURL(this.platformPrefix, 'api', environment.production, this.currentPlatform.domainExtension);
        let baseURLTwo = CommonUtil.getBaseURL(this.currentPlatform.name, 'platform', environment.production, this.currentPlatform.domainExtension);
        this.authenticationService.adminPlatformLogIn(this.platformPrefix, this.centralAccessToken, this.centralRefreshToken, expiresIn, baseURL).subscribe(_adminResult => {
            // @ts-ignore
            let platformForm: HTMLFormElement = document.getElementById('goToPlatformForm');
            platformForm.action = baseURLTwo + '/index.php';
            platformForm['central_access_token'].value = this.centralAccessToken;
            platformForm['central_refresh_token'].value = this.centralRefreshToken;
            platformForm['access_token'].value = _adminResult.access_token;
            platformForm['refresh_token'].value = _adminResult.refresh_token;
            platformForm['expires_in'].value = _adminResult.expires_in;
            platformForm['force_token_refresh'].value = 'true';
            platformForm.submit();
        });
    }

    resetEmail() {
        this.loginFormFirstStep.setValue({'email' : null});
        this.loginFailure = false;
        this.emailGiven = false;
        this.emailErrorNotVerified = false;
        this.emailErrorNotActive = false;
        this.emailErrorNotExisting = false;
        this.emailError = false;
        setTimeout(function() {
            document.getElementById('email').focus();
        }, 5);
    }

    goToCreateAccount() {
        this.router.navigate(['central-login/create-account'], { queryParams: { platform: this.platformPrefix }});
    }

    goToForgotPassword() {
        this.router.navigate(['central-login/forgot-password'], { queryParams: { platform: this.platformPrefix, email: this.email }});
    }

    goToContactAdmin() {
        this.router.navigate(['central-login/contact-admin'], { queryParams: { platform: this.platformPrefix, email: this.email }});
    }
}
